import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, Clock, Edit, FileText, FolderOpen, Inbox, Plus, Send, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MailTopBar = ({
  accountStats, changeFolder, currentFolder, deleteFolder, dragOverFolderId,
  emailAccounts, emailFolders, folderMenuOpen, handleFolderDragLeave, handleFolderDragOver,
  handleFolderDrop, openRenameModal, processBulkEmails, selectedAccount,
  selectedFolderId, setCurrentFolder, setFolderMenuOpen, setSelectedAccount,
  setSelectedFolderId, setShowCreateFolderModal, unreadCount
}) => {
  const { t } = useTranslation('admin');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const btnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // Calcule la position fixe au moment de l'ouverture
  const handleToggleDropdown = () => {
    if (!dropdownOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setDropdownOpen(o => !o);
  };

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handle = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);
  return (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
            {/* Première ligne : Compte + Onglets */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              {/* Account Selector */}
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-slate-600 shrink-0">{t('mail.account')}</label>
                <select
                  data-testid="mail-account-select"
                  value={selectedAccount?.id || ''}
                  onChange={(e) => {
                    const account = emailAccounts.find(a => a.id === e.target.value);
                    setSelectedAccount(account);
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  {emailAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.email_address}
                    </option>
                  ))}
                </select>
              </div>

              {/* Folders Navigation - Horizontal */}
              <nav className="flex items-center space-x-1 overflow-x-auto pb-1 max-w-full [&>button]:flex-shrink-0 [&>button]:whitespace-nowrap">
                <button
                  data-testid="mail-folder-inbox"
                  onClick={() => changeFolder('inbox')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'inbox'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Inbox size={18} className="mr-2" />
                  <span>{t('mail.folderInbox')}</span>
                  {unreadCount > 0 && currentFolder === 'inbox' && (
                    <span className="ml-2 bg-white text-indigo-600 text-xs rounded-full px-2 py-0.5 font-bold">
                      {unreadCount}
                    </span>
                  )}
                </button>

                <button
                  data-testid="mail-folder-sent"
                  onClick={() => changeFolder('sent')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'sent'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Send size={18} className="mr-2" />
                  <span>{t('mail.folderSent')}</span>
                </button>

                <button
                  data-testid="mail-folder-scheduled"
                  onClick={() => changeFolder('scheduled')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'scheduled'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Clock size={18} className="mr-2" />
                  <span>{t('mail.folderScheduled')}</span>
                </button>

                <button
                  data-testid="mail-folder-drafts"
                  onClick={() => changeFolder('drafts')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'drafts'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText size={18} className="mr-2" />
                  <span>{t('mail.folderDrafts')}</span>
                </button>

                <button
                  data-testid="mail-folder-spam"
                  onClick={() => changeFolder('spam')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'spam'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <AlertCircle size={18} className="mr-2" />
                  <span>{t('mail.folderSpam')}</span>
                </button>

                <button
                  data-testid="mail-folder-trash"
                  onClick={() => changeFolder('trash')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'trash'
                      ? 'bg-indigo-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Trash2 size={18} className="mr-2" />
                  <span>{t('mail.folderTrash')}</span>
                </button>

                <button
                  data-testid="mail-folder-processed"
                  onClick={() => changeFolder('processed')}
                  className={`flex items-center px-4 py-2 rounded-xl transition-all ${
                    currentFolder === 'processed'
                      ? 'bg-emerald-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <CheckCircle size={18} className="mr-2" />
                  <span>{t('mail.folderProcessed')}</span>
                </button>

                {/* Bouton Traiter +7j — juste après Traités */}
                {selectedAccount && (
                  <button
                    data-testid="mail-bulk-process-7d"
                    onClick={() => processBulkEmails(7)}
                    className="flex items-center px-3 py-2 rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all text-sm font-medium flex-shrink-0"
                    title={t('mail.process7dTitle')}
                  >
                    <CheckCircle size={16} className="mr-1.5" />
                    <span>{t('mail.process7d')}</span>
                  </button>
                )}

                {/* Séparateur */}
                {selectedAccount && (
                  <span className="w-px h-5 bg-slate-200 mx-1 flex-shrink-0" />
                )}

                {/* Menu déroulant Dossiers — juste après Traités */}
                {selectedAccount && (
                  <div className="relative flex-shrink-0">
                    <button
                      ref={btnRef}
                      data-testid="mail-folders-dropdown-btn"
                      onClick={handleToggleDropdown}
                      className={`flex items-center px-4 py-2 rounded-xl transition-all font-medium ${
                        selectedFolderId
                          ? 'bg-purple-600 text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <FolderOpen size={18} className="mr-2" />
                      <span>{t('mail.foldersMenu', 'Dossiers')}</span>
                      {selectedFolderId && (
                        <span className="ml-2 bg-purple-400 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                          {emailFolders.find(f => f.id === selectedFolderId)?.email_count || ''}
                        </span>
                      )}
                      <ChevronDown size={14} className={`ml-1 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {dropdownOpen && (
                      <div
                        ref={dropdownRef}
                        style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, zIndex: 9999, maxWidth: 'min(320px, 90vw)' }}
                        className="bg-white rounded-xl shadow-xl border border-slate-200 py-1 min-w-[220px]"
                      >
                        {/* Liste des dossiers */}
                        {emailFolders.length === 0 && (
                          <div className="px-3 py-2 text-sm text-slate-400 text-center italic">
                            {t('mail.noFolders', 'Aucun dossier')}
                          </div>
                        )}

                        {emailFolders.map(folder => (
                          <div
                            key={folder.id}
                            className={`flex items-center group transition-colors ${
                              dragOverFolderId === folder.id ? 'bg-purple-50' : ''
                            }`}
                            onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                            onDragLeave={handleFolderDragLeave}
                            onDrop={(e) => handleFolderDrop(e, folder.id)}
                          >
                            <button
                              onClick={() => {
                                if (selectedFolderId === folder.id) {
                                  setSelectedFolderId(null);
                                } else {
                                  setSelectedFolderId(folder.id);
                                  setCurrentFolder('inbox');
                                }
                                setFolderMenuOpen(null);
                                setDropdownOpen(false);
                              }}
                              className={`flex-1 flex items-center px-3 py-2 text-sm text-left transition-colors ${
                                selectedFolderId === folder.id
                                  ? 'bg-purple-50 text-purple-700 font-semibold'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                              title={t('mail.folderTitle', { name: folder.name })}
                            >
                              <FolderOpen size={15} className={`mr-2 ${selectedFolderId === folder.id ? 'text-purple-600' : 'text-purple-400'}`} />
                              <span className="flex-1 truncate">{folder.name}</span>
                              {folder.email_count > 0 && (
                                <span className={`ml-2 text-xs rounded-full px-2 py-0.5 font-bold ${
                                  selectedFolderId === folder.id
                                    ? 'bg-purple-200 text-purple-700'
                                    : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {folder.email_count}
                                </span>
                              )}
                            </button>

                            {/* Actions inline (hover) */}
                            <div className="flex items-center pr-1.5 gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); openRenameModal(folder); setDropdownOpen(false); }}
                                className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                title="Renommer"
                              >
                                <Edit size={13} />
                              </button>
                              {!folder.is_default && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); setFolderMenuOpen(null); }}
                                  className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                                  title={t('mail.delete')}
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Nouveau dossier en bas */}
                        <div className="border-t border-slate-100 mt-1 pt-1">
                          <button
                            data-testid="mail-folder-create-btn"
                            onClick={() => { setShowCreateFolderModal(true); setDropdownOpen(false); }}
                            className="w-full flex items-center px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors font-medium"
                          >
                            <Plus size={15} className="mr-2" />
                            {t('mail.createFolder', 'Nouveau dossier')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </nav>
            </div>
          </div>

  );
};

export default MailTopBar;
